"use client";

import { useContext } from "react";
import { InitialContext, _getPendingPromise } from "./initial-context-provider";
import * as Sentry from "@sentry/nextjs";

/**
 * Hook to access the initial context.
 * Suspends (via React Suspense) until context is available on the client.
 * Must be used within a Suspense boundary.
 */
export function useInitialContext<T>(): T {
  const context = useContext(InitialContext);

  Sentry.addBreadcrumb({
    category: "initial-context",
    message: "useInitialContext",
    level: "info",
    data: {
      context: context ? "context found" : "context not found",
      error: context?.error ? "error found" : "error not found",
      data: context?.data ? "data found" : "data not found",
      ssr: typeof window === "undefined",
    },
  });

  if (context === undefined || context === null) {
    throw new Error(
      "useInitialContext must be used within an InitialContextProvider"
    );
  } else if (context && context.error) {
    throw context.error;
  } else if (context.data === null && typeof window !== "undefined") {
    // Throw promise for Suspense instead of error
    // React Suspense will catch this and show the loading fallback
    throw _getPendingPromise();
  }

  return context.data as T;
}

